/**
 * トランプ記号をFont Awesomeアイコンに置換
 * 
 * @param {string} text - 置換対象のテキスト
 * @returns {string} アイコンタグに置換されたテキスト
 */
function replaceCardSymbols(text) {
    if (!text) return text;

    const symbolMap = {
        // Unicode記号
        '♠': '<i class="fas fa-spade"></i>',
        '♥': '<i class="fas fa-heart"></i>',
        '♦': '<i class="fas fa-diamond"></i>',
        '♣': '<i class="fas fa-club"></i>',
        // スペードの別表記
        '♤': '<i class="fas fa-spade"></i>',
        // ハートの別表記
        '♡': '<i class="fas fa-heart"></i>',
        // ダイヤの別表記
        '♢': '<i class="fas fa-diamond"></i>',
        // クラブの別表記
        '♧': '<i class="fas fa-club"></i>',
        // 角括弧形式（バックエンドから返される形式）
        '[S]': '<i class="fas fa-spade"></i>',
        '[H]': '<i class="fas fa-heart"></i>',
        '[D]': '<i class="fas fa-diamond"></i>',
        '[C]': '<i class="fas fa-club"></i>'
    };

    let result = text;
    for (const [symbol, icon] of Object.entries(symbolMap)) {
        result = result.replace(new RegExp(symbol.replace(/[[\]]/g, '\\$&'), 'g'), icon);
    }

    return result;
}

/**
 * HTMLコンテンツをJSON構造化データに変換するユーティリティ
 * 
 * @param {string} htmlString - 変換するHTML文字列
 * @returns {Object} JSON構造化データ
 */
export function htmlToJson(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    const sections = [];
    let currentSection = null;

    // すべての子要素を走査
    const elements = doc.body.children;

    for (let i = 0; i < elements.length; i++) {
        const element = elements[i];
        const tagName = element.tagName.toLowerCase();

        // セクション区切り（space-80クラスを持つdiv）
        if (element.classList.contains('space-80')) {
            if (currentSection) {
                sections.push(currentSection);
            }
            currentSection = {
                id: element.id || `section_${sections.length + 1}`,
                type: 'section',
                content: []
            };
            continue;
        }

        // 現在のセクションがない場合は作成
        if (!currentSection) {
            currentSection = {
                id: `section_${sections.length + 1}`,
                type: 'section',
                content: []
            };
        }

        // 要素タイプに応じて処理
        const contentItem = parseElement(element);
        if (contentItem) {
            currentSection.content.push(contentItem);
        }
    }

    // 最後のセクションを追加
    if (currentSection && currentSection.content.length > 0) {
        sections.push(currentSection);
    }

    return { sections };
}

/**
 * 個別の要素をJSONオブジェクトに変換
 */
function parseElement(element) {
    const tagName = element.tagName.toLowerCase();

    switch (tagName) {
        case 'h6':
            return {
                type: 'heading',
                level: element.classList.contains('with-title-dark') ? 1 : 2,
                text: replaceCardSymbols(element.textContent.trim()),
                className: element.className
            };

        case 'h5':
            return {
                type: 'heading',
                level: 3,
                text: replaceCardSymbols(element.textContent.trim()),
                className: element.className
            };

        case 'p':
            return {
                type: 'paragraph',
                text: replaceCardSymbols(element.innerHTML.trim()),
                className: element.className
            };

        case 'img':
            return {
                type: 'image',
                src: element.getAttribute('src'),
                alt: element.getAttribute('alt') || '',
                style: element.getAttribute('style') || '',
                className: element.className
            };

        case 'div':
            // カードグリッドの検出
            if (element.classList.contains('grid-container') || element.classList.contains('grid-container2')) {
                return parseCardGrid(element);
            }
            // text-centerなどの特殊divは保持
            if (element.classList.contains('text-center')) {
                return {
                    type: 'div',
                    className: element.className,
                    html: replaceCardSymbols(element.innerHTML)
                };
            }
            // その他のdiv
            return {
                type: 'div',
                className: element.className,
                html: replaceCardSymbols(element.innerHTML)
            };

        case 'ul':
        case 'ol':
            return {
                type: tagName === 'ul' ? 'unordered-list' : 'ordered-list',
                items: Array.from(element.querySelectorAll('li')).map(li => replaceCardSymbols(li.innerHTML.trim())),
                className: element.className
            };

        default:
            // その他の要素はHTMLとして保持
            return {
                type: 'html',
                tag: tagName,
                html: replaceCardSymbols(element.outerHTML)
            };
    }
}

/**
 * カードグリッドをパース
 */
function parseCardGrid(gridContainer) {
    const cards = [];
    const gridItems = gridContainer.querySelectorAll('.grid');

    gridItems.forEach(gridItem => {
        const cardElement = gridItem.querySelector('card');
        if (cardElement) {
            const img = cardElement.querySelector('img');
            const title = cardElement.querySelector('h5');
            const description = cardElement.querySelector('p');

            cards.push({
                image: img ? img.getAttribute('src') : null,
                title: title ? replaceCardSymbols(title.textContent.trim()) : '',
                description: description ? replaceCardSymbols(description.textContent.trim()) : ''
            });
        }
    });

    return {
        type: 'card-grid',
        className: gridContainer.className,
        cards
    };
}

/**
 * 目次を抽出
 */
export function extractTableOfContents(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    const tocItems = [];
    const tocList = doc.querySelector('.table_of_contents ul');

    if (tocList) {
        const items = tocList.querySelectorAll('li');
        items.forEach((item, index) => {
            tocItems.push({
                id: `rule_${index + 1}`,
                label: item.textContent.trim()
            });
        });
    }

    return tocItems;
}
